import { Modal, Input, message } from 'antd';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { BATCH_DELETE_ADDRESS_WHITELIST } from '../graphql/addressWhitelistGraphql';

interface BatchDeleteWhitelistModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

// 解析文本域：按英文逗号切分 → 去空白 → 去空项 → 校验均为正整数
// 返回 { ids, invalid }：ids 为合法 id 列表，invalid 为非法片段列表
const parseIds = (raw: string): { ids: number[]; invalid: string[] } => {
  const ids: number[] = [];
  const invalid: string[] = [];
  raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .forEach((s) => {
      if (/^\d+$/.test(s) && Number(s) > 0) {
        ids.push(Number(s));
      } else {
        invalid.push(s);
      }
    });
  return { ids, invalid };
};

const BatchDeleteWhitelistModal: React.FC<BatchDeleteWhitelistModalProps> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');

  const [batchDeleteWhitelist, { loading }] = useMutation(BATCH_DELETE_ADDRESS_WHITELIST);

  // 弹窗关闭时重置输入，避免下次打开沿用上次内容
  useEffect(() => {
    if (!visible) {
      setInputValue('');
    }
  }, [visible]);

  const handleSubmit = () => {
    const { ids, invalid } = parseIds(inputValue);

    if (invalid.length > 0) {
      message.error(
        t('mapTool.addressWhitelist.batchDelete.invalidIds', { ids: invalid.join('、') }) as string
      );
      return;
    }
    if (ids.length === 0) {
      message.error(t('mapTool.addressWhitelist.batchDelete.emptyIds') as string);
      return;
    }

    const uniqueIds = Array.from(new Set(ids));

    Modal.confirm({
      title: t('mapTool.addressWhitelist.deleteTitle') as string,
      content: t('mapTool.addressWhitelist.batchDelete.confirmContent', {
        count: uniqueIds.length,
      }) as string,
      okText: t('components.button.confirm') as string,
      okType: 'danger',
      cancelText: t('components.button.cancel') as string,
      onOk: async () => {
        try {
          const { data } = await batchDeleteWhitelist({ variables: { ids: uniqueIds } });
          const deleted = data?.batchDeleteAddressWhitelist ?? 0;
          message.success(
            t('mapTool.addressWhitelist.batchDelete.successMsg', {
              requested: uniqueIds.length,
              deleted,
            }) as string
          );
          onClose();
          onSuccess?.();
        } catch (err) {
          message.error(
            t('mapTool.addressWhitelist.batchDelete.failMsg', {
              msg: (err as Error).message,
            }) as string
          );
        }
      },
    });
  };

  return (
    <Modal
      title={t('mapTool.addressWhitelist.batchDelete.modalTitle')}
      open={visible}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={t('components.button.delete')}
      okButtonProps={{ danger: true, loading }}
      cancelText={t('components.button.cancel')}
      width={600}
      destroyOnClose
    >
      <div style={{ marginBottom: 8 }}>
        {t('mapTool.addressWhitelist.batchDelete.inputLabel')}
      </div>
      <Input.TextArea
        placeholder={t('mapTool.addressWhitelist.batchDelete.placeholder') as string}
        autoSize={{ minRows: 6, maxRows: 12 }}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
      />
    </Modal>
  );
};

export default BatchDeleteWhitelistModal;
